import { useContext, useState, useEffect } from 'react';
import PageContext from './PageContext';
import PageDropdown from './PageDropdownCopy.jsx';
import AutoCompleteInputWithOptions from '../sub-components/AutoCompleteInputWithOptionsCopy.jsx';
import EditEntitlementModalRow from './EditEntitlementModalRow';
import { HttpContext } from '../../api-services/HttpService';
import OrderedDataTable from '../sub-components/OrderedDataTableCopy';
import ApiRequestButton from './ApiRequestButton';
import CCOffcanvas from "./CCOffcanvas.tsx";
import ButtonComponent from "./ButtonComponent.jsx";

const UserEntitlementModal = () => {
    // get context variables from parent
    const {
        editModal,
        setEditModal,
        editUser,
        getInfo,
        snackbarAlert,
        getUserEntitlementsCounts
    } = useContext(PageContext);

    // initialize http context 
    const {
        GET,
        POST,
    } = useContext(HttpContext);

    // state for role dropdown
    const [role, setRole] = useState('--select--');
    const [roleList, setRoleList] = useState([]);
    // state for company autocomplete
    const [searchCompany, setSearchCompany] = useState('');
    const [companyObj, setCompanyObj] = useState({ label: '', id: null })
    const [companyList, setCompanyList] = useState(['']);
    // state for entitlement table
    const [tableHeader, setTableHeader] = useState([]);
    const [showDataList, setShowDataList] = useState([]);
    // initialize spinner state
    const [showSpinner, setShowSpinner] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    // create function for getting/reading user entitlement data from DB, function takes in 1 userid and returns a promise
    const getUserEntitlements = (user) => {
        setShowSpinner(showSpinner => showSpinner = true);
        return new Promise((resolve, reject) => {
            GET('/user/entitlements', { userid: user }).then(res => {
                // set table state
                setShowDataList(showDataList => showDataList = res.data.UserEntitlements);
                setTableHeader(tableHeader => tableHeader = res['data']['GuiTableCols']);
                // set dropdown state 
                setRoleList(roleList => roleList = ['--select--', ...res.data.UserRolesLookup.map(role => role.userroledescription)]);
                setCompanyList(companyList => companyList = [...res.data.CompaniesLookup]);
                // set showSpinner to false
                setShowSpinner(showSpinner => showSpinner = false);
                resolve(res);
            }).catch(err => {
                setShowSpinner(showSpinner => showSpinner = false);
                reject(err);
            });
        });
    }

    // create function for posting/creating a new user entitlement data in the DB, function takes in 1 userid and returns a promise
    const postUserEntitlements = () => {
        return new Promise((resolve, reject) => {
            let response;
            // check that role is not select
            if (role == '--select--') {
                snackbarAlert(true, 'Please choose role for entitlement.', 'error');
                reject();
                return;
            }
            // check that company has been selected
            if (companyObj['id'] == null) {
                snackbarAlert(true, 'Please choose company for entitlement.', 'error');
                reject();
                return;
            }
            // use POST function to make request
            POST('/data/companyEntitlements', {
                userid: editUser,
                mastercompanyid: companyObj['id'],
                userroledescription: role,
                active: true,
            }).then(res => {
                console.log(res);
                response = res;
                // show snackbar on success
                snackbarAlert(true, `Created New Entitlement For ${editUser}`, 'success');
                // default state
                setRole(role => role = '--select--');
                setSearchCompany(searchCompany => searchCompany = '');
                setCompanyObj(companyObj => companyObj = { label: '', id: 0 });
                // refresh data for user 
                return getUserEntitlements(editUser);
            }).then(res => {
                console.log(res);
                // refresh counts data
                return getUserEntitlementsCounts();
            }).then(res => {
                console.log(res);
                // resolve on success
                resolve(res);
            }).catch(err => {
                console.log(err);
                // show snackbar on error
                snackbarAlert(true, `Failed to Create Entitlement For ${editUser}`, 'error');
                // reject on error
                reject(err);
            });
        });
    }

    // function wrapper to pass row component to datatable
    const RowComponent = (row, tableHeader) => {
        return (
            <>
                <EditEntitlementModalRow row={row} tableHeader={tableHeader} />
            </>
        );
    }

    // useEffect to run when modal is toggled
    useEffect(() => {
        if (editModal) {
            //get entitlement data for user selected on GUI
            getUserEntitlements(editUser).then(res => {
                console.log(res);
            }).catch(err => {
                console.log(err);
            });
            //reset dropdowns
            setRole(role => role = '--select--');
            setSearchCompany(searchCompany => searchCompany = '');
        }
    }, [editModal]);


    const settings = {
        roleList,
        editUser,
        getUserEntitlements,
        snackbarAlert,
        tableHeader,
        getUserEntitlementsCounts
    };

    const isDisabledFunc = () => {
        if(role !== '--select--' && companyObj.label !== ''){
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }

    useEffect(() => {
        isDisabledFunc();
    }, [role,companyObj])

    return (
        <PageContext.Provider value={settings}>
            <>
                <CCOffcanvas
                    title={`Entitlements for ${editUser}`}
                    show={editModal}
                    onCanvasClose={() => setEditModal(editModal => editModal = false)}
                    bodyCssClassName="offcanvas-body"
                    offCanvasClassName="offcanvas-parent-card"
                >
                     <div className="d-flex flex-column h-100">
                        {/* read only input for info on rebate workflow that is being viewed */}
                        <div className="bg-white p-3" style={{ fontSize: "14px" }}>
                            <div className='mb-3'>Add Entitlement</div>
                            <div className="d-flex flex-wrap">
                                <div className="me-3 mt-1">
                                    <PageDropdown tag='Role' value={role} setValue={setRole} list={roleList} />
                                </div>
                                <div className="me-3">
                                    <AutoCompleteInputWithOptions tag='Company' value={searchCompany} setValue={setSearchCompany} obj={companyObj} setObj={setCompanyObj} list={companyList} width='250px' />
                                </div>
                                <div>
                                    <ApiRequestButton onButtonClick={postUserEntitlements} borderColor={'#3D3D3D 1px solid'} backgroundColor={'#fff'} textColor={'#3D3D3D'} disabledBorderColor={'#BDBDBD 1px solid'} disabledBackgroundColor={'#D6D6D6'} disabledTextColor={'#BDBDBD'} disabled={isDisabled}/>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 overflow-scroll">
                            <div className="p-3 trail-section">
                                <div className='mb-3'>Edit/Remove Entitlements</div>
                                <div className="bg-white trail-card">
                                    <OrderedDataTable RowComponent={RowComponent} disableMaxTableHeight={true}
                                        stickyFirstColumnEnabled={true} tableHeader={tableHeader} dataList={showDataList} showSpinner={showSpinner} numToPage={10} skeletonHeight='28vh' />
                                </div>
                            </div>

                        </div>
                        <div
                            className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{height: '65px'}}
                        >
                            <ButtonComponent
                                tag={"OK"}
                                border={'#fcd800 1px solid'}
                                background={'#fcd800'}
                                color={'#001317'}
                                onButtonClick={() => { setEditModal(editModal => editModal = false) }
                                }
                            />
                        </div>
                    </div>
                </CCOffcanvas>

            </>
        </PageContext.Provider>
    );
}

export default UserEntitlementModal;